// ConvoCX unified inbox — auth context (CON-845).
// Manages the JWT session: login, logout, token persistence, and the
// "no session" gate that shows a login view before the inbox mounts.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, getToken, setToken } from './api';
import type { LoginResponse } from './types';

interface AuthContextValue {
  token: string | null;
  user: LoginResponse['user'] | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, remember?: boolean) => Promise<boolean>;
  register: (payload: { accountName: string; email: string; password: string; displayName?: string }) => Promise<boolean>;
  createWorkspace: (name: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  token: null,
  user: null,
  loading: true,
  error: null,
  login: async () => false,
  register: async () => false,
  createWorkspace: async () => false,
  logout: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<LoginResponse['user'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Restore the authoritative profile from GET /api/auth/me. The JWT payload
    // carries no `role`, so rebuilding identity from it alone made the sidebar
    // fall back to the reduced (agent) menu on every reload. Decoding the JWT
    // stays as a last-resort fallback when /me is unavailable.
    api
      .me()
      .then((res) => {
        if (cancelled) return;
        // Skip when login/register already seeded the user this session.
        setUser((prev) =>
          prev ?? {
            id: res.user.id,
            email: res.user.email,
            displayName: res.user.displayName || res.user.email,
            role: res.user.role,
            accountId: res.account.id,
            accountName: res.account.name,
            plan: res.account.plan,
          },
        );
      })
      .catch(() => {
        try {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1])) as { userId?: string; email?: string };
            if (!cancelled) {
              setUser((prev) => prev ?? { id: payload.userId ?? '', email: payload.email ?? '', displayName: payload.email ?? '', role: '', accountId: '', accountName: '', plan: '' });
            }
          }
        } catch {
          // not a JWT we can decode — leave user null
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email: string, password: string, remember = true) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.login(email, password);
      setToken(res.token, remember);
      setTokenState(res.token);
      setUser(res.user);
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload: { accountName: string; email: string; password: string; displayName?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.register(payload);
      setToken(res.token, true);
      setTokenState(res.token);
      setUser(res.user);
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Sign-up failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const createWorkspace = useCallback(async (name: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.createWorkspace(name);
      setToken(res.token, true);
      setTokenState(res.token);
      setUser(res.user);
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Workspace creation failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setTokenState(null);
    setUser(null);
    setError(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, loading, error, login, register, createWorkspace, logout }),
    [token, user, loading, error, login, register, createWorkspace, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}